import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { GridColDef } from '@mui/x-data-grid/models';
import { Box, IconButton, Tooltip, Button, Badge } from '@mui/material';
import { Add, Search, Refresh, GetApp } from '@mui/icons-material';
import { SearchServicesRequest, ServiceResponse, ACLAuthorizationRequest } from '@/api/bol';
import { parseJsonWithDates } from '@/utils/jsonUtils';
import { 
  PageLayout, 
  DataGridContainer, 
  JsonDetailPanel,
  SearchPanel,
  createServiceColumns,
  defaultServiceColumnVisibilityModel,
  ServiceFormDialog,
  ServiceDeleteDialog,
  GenericCSVExportDialog,
} from '@/components';
import { 
  useDataGridSelection, 
  usePagination, 
  useSnackbarNotification,
  useServices,
  useStableRowId,
  usePermissions,
  useLogisticsObjectSearchUrl,
  getActionTooltip
} from '@/hooks';
import { exportToCSV, generateCSVFilename } from '@/utils/csvExport';
import { DEFAULT_LOGISTICS_OBJECT_SEARCH_QUERY } from '@/utils/searchDefaults';
import { getDefaultSavedQuery } from '@/utils/savedQueriesStorage';

const ServicesPage: React.FC = () => {
  const { can } = usePermissions();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  // Use custom hooks
  const {
    services,
    loading,
    selectedService,
    setSelectedService,
    fetchServices,
    createService,
    deleteService,
    total
  } = useServices();

  const { 
    selectedItem: selectedServiceForDetail, 
    selectedRowId,
    handleRowSelectionChange,
    handleCloseDetailPanel 
  } = useDataGridSelection<ServiceResponse>({ 
    data: services, 
    getItemId: (item) => item.id || '' 
  });

  const {
    pagination,
    handlePaginationModelChange,
  } = usePagination({
    onPageChange: (page, pageSize) => {
      void fetchServices(page, pageSize, currentSearchQuery);
    }
  });

  const {
    snackbarOpen,
    snackbarMessage,
    snackbarLink,
    snackbarType,
    showSuccess,
    showError,
    closeSnackbar
  } = useSnackbarNotification();

  const {
    getSearchQueryFromUrl,
    updateSearchQueryInUrl,
    copyShareableSearchLink,
  } = useLogisticsObjectSearchUrl({ queryParamName: 'search' });

  // Search panel state
  const [searchPanelOpen, setSearchPanelOpen] = useState(false);
  const initialSearchQuery = useMemo<SearchServicesRequest>(
    () => {
      const searchQueryFromUrl = getSearchQueryFromUrl() as SearchServicesRequest | undefined;

      return searchQueryFromUrl
        || getDefaultSavedQuery('service')?.query as SearchServicesRequest
        || DEFAULT_LOGISTICS_OBJECT_SEARCH_QUERY;
    },
    [getSearchQueryFromUrl],
  );
  const [currentSearchQuery, setCurrentSearchQuery] = useState<SearchServicesRequest>(initialSearchQuery);

  const hasAppliedQueryOrSort = useMemo(() => {
    const hasQueryContent = !!currentSearchQuery?.query
      && typeof currentSearchQuery.query === 'object'
      && Object.keys(currentSearchQuery.query).length > 0;
    const hasSortContent = Array.isArray(currentSearchQuery?.sort)
      && currentSearchQuery.sort.length > 0;

    return hasQueryContent || hasSortContent;
  }, [currentSearchQuery]);

  const displayColumnsRef = useRef<{ columns: GridColDef[]; visibilityModel: Record<string, boolean> }>({
    columns: [],
    visibilityModel: defaultServiceColumnVisibilityModel,
  });

  const handleDisplayColumnsChange = useCallback((cols: GridColDef[], visibilityModel: Record<string, boolean>) => {
    displayColumnsRef.current = { columns: cols, visibilityModel };
  }, []);

  const getRowId = useStableRowId<ServiceResponse>('service');

  // Event handlers
  const handleCreateService = async (data: { memberIdentifier: string; serviceJson: string; aclAuthorizations?: ACLAuthorizationRequest[] }) => {
    try {
      // Validate required fields
      if (!data.memberIdentifier?.trim()) {
        throw new Error('Member identifier is required');
      }
      
      if (!data.serviceJson?.trim()) {
        throw new Error('Service JSON is required');
      }

      // Parse and validate JSON
      let serviceData;
      try {
        serviceData = parseJsonWithDates(data.serviceJson);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {
        throw new Error('Invalid JSON format. Please check your JSON syntax.');
      }

      // Validate parsed data structure
      if (!serviceData || typeof serviceData !== 'object') {
        throw new Error('Service data must be a valid JSON object');
      }

      await createService(serviceData, data.memberIdentifier, data.aclAuthorizations);
      setCreateDialogOpen(false);
      fetchServices(pagination.page, pagination.pageSize, currentSearchQuery);
    } catch (error) {
      console.error('Service creation validation error:', error);
      throw error; // Re-throw to let the dialog handle the error display
    }
  };

  const handleDeleteService = async () => {
    if (selectedService?.id) {
      await deleteService(selectedService.id);
      setSelectedService(null);
      setDeleteDialogOpen(false);
      fetchServices(pagination.page, pagination.pageSize, currentSearchQuery);
    }
  };

  const handleDeleteClick = (service: ServiceResponse) => {
    setSelectedService(service);
    setDeleteDialogOpen(true);
  };

  const handleRefresh = () => {
    fetchServices(pagination.page, pagination.pageSize, currentSearchQuery);
  };

  const handleSearch = async (searchData: unknown) => {
    const searchBody = searchData as SearchServicesRequest;
    setCurrentSearchQuery(searchBody);
    const isSearchSuccessful = await fetchServices(0, pagination.pageSize, searchBody); // Reset to first page on search

    if (isSearchSuccessful) {
      updateSearchQueryInUrl(searchBody);
    }
  };

  const handleSearchReset = async () => {
    const defaultSearchQuery = (getDefaultSavedQuery('service')?.query as SearchServicesRequest)
      || DEFAULT_LOGISTICS_OBJECT_SEARCH_QUERY;
    setCurrentSearchQuery(defaultSearchQuery);
    const isResetSuccessful = await fetchServices(0, pagination.pageSize, defaultSearchQuery); // Reset to first page with default search

    if (isResetSuccessful) {
      updateSearchQueryInUrl(defaultSearchQuery);
    }
  };

  const handleCopySearchLink = useCallback(async () => {
    await copyShareableSearchLink(currentSearchQuery, {
      onSuccess: showSuccess,
      onError: showError,
    });
  }, [copyShareableSearchLink, currentSearchQuery, showError, showSuccess]);

  const handleToggleSearchPanel = () => {
    setSearchPanelOpen(!searchPanelOpen);
  };

  const handleExportCSV = (separator: string) => {
    exportToCSV({
      data: services,
      columns: displayColumnsRef.current.columns,
      columnVisibilityModel: displayColumnsRef.current.visibilityModel,
      filename: generateCSVFilename('services'),
      separator,
    });
  };

  const columns = createServiceColumns({
    onDelete: handleDeleteClick,
    canDelete: can.deleteLogisticsObject,
    deleteTooltip: getActionTooltip('deleteLogisticsObject')
  });

  useEffect(() => {
    fetchServices(0, 10, initialSearchQuery);
  }, [fetchServices, initialSearchQuery]);

  return (
    <PageLayout 
      title="Services Management"
      selectedItemInfo={selectedServiceForDetail ? `Selected: ${selectedServiceForDetail.id}` : undefined}
      notificationOpen={snackbarOpen}
      notificationMessage={snackbarMessage}
      notificationLink={snackbarLink}
      notificationType={snackbarType}
      onNotificationClose={closeSnackbar}
      leftTitleActions={
        <>
          <Tooltip title="Refresh services list">
            <IconButton
              onClick={handleRefresh}
              size="small"
            >
              <Refresh />
            </IconButton>
          </Tooltip>
          <Tooltip title={searchPanelOpen ? "Close search panel" : "Open search panel"}>
            <IconButton
              onClick={handleToggleSearchPanel}
              color={searchPanelOpen ? "primary" : "default"}
              size="small"
            >
              <Badge color="secondary" variant="dot" invisible={!hasAppliedQueryOrSort}>
                <Search />
              </Badge>
            </IconButton>
          </Tooltip>
        </>
      }
      titleActions={
            <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<GetApp />}
            onClick={() => setExportDialogOpen(true)}
            size="medium"
          >
            Export CSV
          </Button>
          <Tooltip title={!can.createLogisticsObject ? getActionTooltip('createLogisticsObject') : ''}>
          <span>
          <Button
                variant="contained"
                size="medium"
                onClick={() => setCreateDialogOpen(true)}
                startIcon={<Add />}
              disabled={!can.createLogisticsObject}
              >
                Create Service
              </Button>
          </span>
        </Tooltip>
        </Box>
      }
      leftPanel={searchPanelOpen ? (
        <SearchPanel
          title="Search Services"
          onClose={() => setSearchPanelOpen(false)}
          onSearch={handleSearch}
          onReset={handleSearchReset}
          onCopyLink={handleCopySearchLink}
          loading={loading}
          logisticsObjectType="service"
          currentSearchQuery={currentSearchQuery}
        />
      ) : undefined}
      sidePanel={selectedServiceForDetail ? (
        <JsonDetailPanel
          title="Service Details"
          data={selectedServiceForDetail}
          onClose={handleCloseDetailPanel}
          onCopyError={() => showError('Failed to copy JSON to clipboard')}
        />
      ) : undefined}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <DataGridContainer
          rows={services}
          columns={columns}
          loading={loading}
          selectedRowId={selectedRowId}
          onRowSelectionChange={handleRowSelectionChange}
          paginationModel={pagination}
          rowCount={total}
          onPaginationModelChange={handlePaginationModelChange}
          getRowId={getRowId}
          columnVisibilityModel={defaultServiceColumnVisibilityModel}
          columnOrderStorageKey="services-columns"
          onDisplayColumnsChange={handleDisplayColumnsChange}
        />
      </Box>

      {/* Create Service Dialog */}
      <ServiceFormDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateService}
      />

      {/* Delete Confirmation Dialog */}
      <ServiceDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteService}
        service={selectedService}
      />

      {/* CSV Export Dialog */}
      <GenericCSVExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        onSubmit={handleExportCSV}
        title="Export Services to CSV"
        entityName="services"
        defaultFilename={generateCSVFilename('services')}
        currentPageInfo={{
          page: pagination.page,
          pageSize: pagination.pageSize,
          totalRows: total,
        }}
      />

      </PageLayout>
    );
  };

export default ServicesPage;